import { useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import ConfirmationDialog from 'components/Dialogs/ConfirmationDialog';
import { bulkActionButtonSx } from '../review-side-panel/ReviewSidePanel.styles';

type SettleAction = 'accept' | 'decline';

interface GridCitationSelectionToolbarProps {
  count: number;
  canSettle: boolean;
  hasStagedCitation: boolean;
  busy?: boolean;
  onAttach: () => void;
  onSettle: (action: SettleAction) => void;
  onClear: () => void;
  onFilterDrawer: () => void;
  onCancel: () => void;
}

const GridCitationSelectionToolbar = ({
  count,
  canSettle,
  hasStagedCitation,
  busy = false,
  onAttach,
  onSettle,
  onClear,
  onFilterDrawer,
  onCancel,
}: GridCitationSelectionToolbarProps) => {
  const [confirmClear, setConfirmClear] = useState(false);
  const empty = count === 0;
  const settleButton = (action: SettleAction, label: string) => (
    <Button
      size="small"
      variant="contained"
      disableElevation
      sx={bulkActionButtonSx(action)}
      disabled={busy || !canSettle}
      onClick={() => onSettle(action)}>
      {label}
    </Button>
  );

  return (
    <>
      <Stack
        role="toolbar"
        aria-label="Selected cell actions"
        data-testid="grid-citation-selection-toolbar"
        direction="row"
        useFlexGap
        spacing={0.5}
        alignItems="center"
        sx={{
          '& > *': { whiteSpace: 'nowrap' },
          // Match the nav's own controls (tabs, Save) so every button reads at one size.
          '& .MuiButton-root': {
            minHeight: 24,
            py: 0,
            textTransform: 'none',
            fontSize: 'var(--tertiary-navigation-font-size, 12px)',
            lineHeight: 'var(--tertiary-navigation-line-height, 16px)',
          },
        }}>
        <Typography variant="caption">{count} selected</Typography>
        {/* Leads the group so its appearance never shifts the right-anchored buttons. */}
        {hasStagedCitation && !empty && (
          <Button size="small" variant="outlined" disabled={busy} onClick={onAttach}>
            Attach Citation
          </Button>
        )}
        {settleButton('accept', 'Accept')}
        {settleButton('decline', 'Reject')}
        <Button size="small" disabled={busy || empty} onClick={() => setConfirmClear(true)}>
          Clear
        </Button>
        <Button size="small" disabled={busy || empty} onClick={onFilterDrawer}>
          Apply Filter
        </Button>
        <Button size="small" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
      <ConfirmationDialog
        open={confirmClear}
        title="Clear citations"
        onClose={() => setConfirmClear(false)}
        actions={[
          { label: 'Cancel', variant: 'text' },
          {
            label: 'Confirm',
            variant: 'contained',
            callback: () => {
              setConfirmClear(false);
              onClear();
            },
          },
        ]}>
        Cell values remain. Selected citations and Excel references will be permanently deleted.
      </ConfirmationDialog>
    </>
  );
};

export default GridCitationSelectionToolbar;
